import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminAuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || '';

const isDevEnvironment = () => {
  const host = window.location.hostname;
  return host.includes('.ts.net') ||
         host === 'localhost' ||
         host === '127.0.0.1';
};

export function AdminAuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    const email = sessionStorage.getItem('adminEmail');
    if (token && email) {
      setIsAuthenticated(true);
      setAdminEmail(email);
    } else if (isDevEnvironment()) {
      // Store dev token for API calls in dev mode
      sessionStorage.setItem('adminToken', 'dev-mode-token');
      sessionStorage.setItem('adminEmail', 'dev@localhost');
      setIsAuthenticated(true);
      setAdminEmail('dev@localhost');
    }
    setLoading(false);
  }, []);

  const requestCode = async (email) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Request code error:', error);
      return { success: false, error: 'Failed to send verification code' };
    }
  };

  const verifyCode = async (email, code) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await response.json();
      
      if (data.success && data.token) {
        sessionStorage.setItem('adminToken', data.token);
        sessionStorage.setItem('adminEmail', email);
        setIsAuthenticated(true);
        setAdminEmail(email);
      }
      
      return data;
    } catch (error) {
      console.error('Verify code error:', error);
      return { success: false, error: 'Failed to verify code' };
    }
  };

  const logout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminEmail');
    setIsAuthenticated(false);
    setAdminEmail(null);
  };

  const value = {
    isAuthenticated,
    adminEmail,
    loading,
    requestCode,
    verifyCode,
    logout
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
